<script setup lang="ts">
import { UserPlus } from 'lucide-vue-next';
import { toast } from 'vue-sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { CLIENT_LIFECYCLE_STATUS_LABELS } from '~/lib/clientLifecycle';
import { buildQuickClientPayload, canQuickCreateClient, createClientQuickDraft } from '~/lib/clientCreation';
import type { ClientLifecycleStatus, ClientSourceType } from '~/types/models';

const props = withDefaults(defineProps<{
    open: boolean;
    initialLifecycleStatus?: ClientLifecycleStatus;
}>(), {
    initialLifecycleStatus: 'raw_enquiry',
});

const emit = defineEmits<{
    (event: 'update:open', value: boolean): void;
    (event: 'created', clientId: string): void;
}>();

const clientStore = useClientStore();
const saving = ref(false);
const draft = ref(createClientQuickDraft({ lifecycleStatus: props.initialLifecycleStatus }));
const canCreate = computed(() => canQuickCreateClient(draft.value) && !saving.value);

const lifecycleOptions = Object.entries(CLIENT_LIFECYCLE_STATUS_LABELS).map(([value, label]) => ({ value, label }));
const sourceOptions: Array<{ value: ClientSourceType; label: string }> = [
    { value: 'manual', label: 'Manual entry' },
    { value: 'website', label: 'Website enquiry' },
    { value: 'referral', label: 'Referral' },
    { value: 'form_submission', label: 'Form submission' },
];

function resetDraft() {
    draft.value = createClientQuickDraft({ lifecycleStatus: props.initialLifecycleStatus });
}

async function createClient() {
    if (!canCreate.value) return;
    saving.value = true;
    try {
        const clientId = await clientStore.addClient(buildQuickClientPayload(draft.value));
        toast.success('Client created.');
        emit('created', clientId);
        emit('update:open', false);
        resetDraft();
    } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Client could not be created.');
    } finally {
        saving.value = false;
    }
}

watch(() => props.open, (open) => {
    if (open) resetDraft();
});

watch(() => props.initialLifecycleStatus, (status) => {
    if (props.open) draft.value.lifecycleStatus = status;
});
</script>

<template>
    <Sheet :open="open" @update:open="emit('update:open', $event)">
        <SheetContent side="right" class="w-full gap-0 p-0 sm:max-w-xl">
            <SheetHeader class="border-b px-6 py-5 text-left">
                <div class="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    <UserPlus class="size-4" />
                    Quick capture
                </div>
                <SheetTitle>New client</SheetTitle>
                <SheetDescription>Create the workspace record now, then enrich it from the Client workspace.</SheetDescription>
            </SheetHeader>

            <div class="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
                <div class="grid gap-4 sm:grid-cols-2">
                    <div class="space-y-2">
                        <Label for="quick-client-company">Company name</Label>
                        <Input id="quick-client-company" v-model="draft.companyName" placeholder="Acme Studio" autofocus />
                    </div>
                    <div class="space-y-2">
                        <Label for="quick-client-contact">Contact name</Label>
                        <Input id="quick-client-contact" v-model="draft.contactName" placeholder="Jamie Smith" />
                    </div>
                </div>

                <div class="grid gap-4 sm:grid-cols-2">
                    <div class="space-y-2">
                        <Label for="quick-client-email">Email</Label>
                        <Input id="quick-client-email" v-model="draft.email" type="email" placeholder="jamie@example.com" />
                    </div>
                    <div class="space-y-2">
                        <Label for="quick-client-phone">Phone</Label>
                        <Input id="quick-client-phone" v-model="draft.phone" placeholder="+44 7700 900000" />
                    </div>
                </div>

                <div class="grid gap-4 sm:grid-cols-2">
                    <div class="space-y-2">
                        <Label>Lifecycle</Label>
                        <SearchableSelectMenu v-model="draft.lifecycleStatus" :options="lifecycleOptions" show-all-by-default placeholder="Choose lifecycle" variant="form" />
                    </div>
                    <div class="space-y-2">
                        <Label>Source</Label>
                        <SearchableSelectMenu v-model="draft.source" :options="sourceOptions" show-all-by-default placeholder="Choose source" variant="form" />
                    </div>
                </div>
            </div>

            <SheetFooter class="sheet-safe-footer border-t px-6 pt-4">
                <Button type="button" variant="outline" :disabled="saving" @click="emit('update:open', false)">Cancel</Button>
                <Button type="button" :disabled="!canCreate" @click="createClient">{{ saving ? 'Creating...' : 'Create client' }}</Button>
            </SheetFooter>
        </SheetContent>
    </Sheet>
</template>
